// @ts-check
import React from 'react';
import { injectIntl } from 'react-intl';
import BusComponent from '../BusComponent';
import AppConfig from '../../appConfig';
import CenterMapLayerButton from './CenterMapLayerButton';
import AreaSelector from './AreaSelector';
import LocationAnalysisReportPopup from './LocationAnalysisReportPopup';
import VisualReportSave from './VisualReportSave';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';
import { mapLocationAnalysisItemToVisualReport } from '../../helpers/Util';

/**
 * @typedef Props
 * @property {import('../../objects/MapInstance').default} mapInstance
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {import('../../types').VisualReport} visualReport
 * @property {boolean} showReport
 * @property {boolean} compare
 * @property {string} helpTourReport
 *
 * @extends {BusComponent<Props, State>}
 */
class ReportIframe extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            visualReport: !!this.props.mapInstance.locationAnalysisItem
                ? mapLocationAnalysisItemToVisualReport(
                      this.props.mapInstance.locationAnalysisItem,
                  )
                : undefined,
            showReport: !!this.props.mapInstance.locationAnalysisItem,
            compare: false,
            helpTourReport: undefined,
        };

        /** @type {HTMLIFrameElement | null} */
        this.iframe = null;
    }

    componentDidMount() {
        this.bindGluBusEvents({
            LOCATION_ANALYSIS_MODE_STARTED: this.startUpdateLocationAnalysis,
            LOCATION_ANALYSIS_MODE_EXITED: this.onExitLocationAnalysis,
            SHOW_INSIGHTS: this.onShowInsights,
            INSIGHTS_STATE_REQUEST: this.onInsightsState,
            INSIGHTS_REPORT_UPDATE_REQUEST: this.onInsightsReportUpdate,
            COLLAPSE_ALL: this.onClose,
        });
        window.addEventListener('message', this.handlePostMessage);
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
        window.removeEventListener('message', this.handlePostMessage);
    }

    /**
     * @param {object} payload
     * @param {string} payload.mapInstanceId
     */
    startUpdateLocationAnalysis = ({ mapInstanceId }) => {
        if (this.props.mapInstance.id !== mapInstanceId) return;

        const visualReport = mapLocationAnalysisItemToVisualReport(
            this.props.mapInstance.locationAnalysisItem,
        );

        this.setState({
            showReport: true,
            visualReport,
        });
    };

    onShowInsights = visualReport => {
        this.setState({ visualReport });
    };

    onExitLocationAnalysis = ({ mapInstanceId }) => {
        if (this.props.mapInstance.id !== mapInstanceId) return;
        this.setState({
            showReport: false,
            visualReport: undefined,
        });
    };

    handlePostMessage = event => {
        if (event.origin !== AppConfig.constants.frontends.insightsBaseUrl) return;
        if (event.data && event.data.url) {
            window.open(event.data.url, '_blank').focus();
        }
    };

    onInsightsState = () => {
        this.emit('INSIGHTS_STATE', { open: this.state.showReport });
    };

    onInsightsReportUpdate = ({ report }) => {
        this.setState({ helpTourReport: report });
        this.emit('INSIGHTS_REPORT_UPDATED');
    };

    getUrl = (frame = false) => {
        const { helpTourReport } = this.state;
        const { point, profile, selection, title, facilityId, pointType, report } =
            this.state.visualReport;

        const profileParam = profile;
        const selectionParam = selection.join(',');

        let configUrl = AppConfig.constants.frontends.insightsReportUrl;
        if (frame) {
            // this is a convention to select view that is suitable for
            // iframe. In visual-report application, App.tsx, it is defined
            // to use /frame suffix to show view more suitable for iframe
            configUrl = configUrl.replace('?', '/frame?');
        }

        // Apply report that will be opened by default
        // Use helpTourReport if set,
        // Else use report if set in visualReport object, otherwise read it from the config
        const urlReport = helpTourReport || report || AppConfig.constants.defaultInsightsReport;
        configUrl = configUrl.replace('{report}', urlReport);

        // Apply mandatory search params
        configUrl = configUrl
            .replace('{pois}', `${point.lng};${point.lat}`)
            .replace('{profile}', profileParam)
            .replace('{selection}', selectionParam)
            .replace('{title}', encodeURIComponent(title));

        // Apply additional search params
        if(facilityId) {
            configUrl = configUrl.concat(`&facility-id=${facilityId}`);
        }
        if(pointType) {
            configUrl = configUrl.concat(`&point-type=${pointType}`);
        }

        return configUrl;
    };

    /** @type {React.Ref<HTMLIFrameElement>} */
    setIframeRef = iframe => {
        this.iframe = iframe;
    };

    onClose = () => {
        this.emit('CLOSE_LOCATION_ANALYSIS_REQUEST', {
            mapInstanceId: this.props.mapInstance.id,
        });
    };

    // TODO: this will always open pricing, instead of the one currently
    // active in the iframe. That needs to be fixed
    onOpenInNewTab = () => {
        this.iframe.contentWindow.postMessage(
            { action: 'openTab' },
            AppConfig.constants.frontends.insightsBaseUrl,
        );
    };

    onEnter = () => {
        this.bus.emit('IFRAME_MOUSE_ENTER');
    };

    render() {
        if (!this.state.showReport) {
            return null;
        }
        const url = this.getUrl(true);

        if (this.state.compare) {
            return (
                <div className="report-iframe report-iframe--fs">
                    <div className="flex-it center flex-end">
                        <button onClick={this.onClose} className="material-icons btn-flat-icon">
                            close
                        </button>
                    </div>
                    <div className="report-iframe__iframe">
                        <iframe src={url} title="Visual report" ref={this.setIframeRef} />
                    </div>
                </div>
            );
        }

        return (
            <div className="report-iframe-wrapper" data-tourId={HelpTourTargets.INFO_PANEL}>
                <div className="report-iframe" onMouseEnter={this.onEnter}>
                    <div className="report-iframe__iframe-header">
                        <AreaSelector
                            mapInstanceId={this.props.mapInstance.id}
                            locationAnalysisItem={this.props.mapInstance.locationAnalysisItem}
                        />
                        <div className="report-iframe__iframe-header--actions">
                            <LocationAnalysisReportPopup mapInstanceId={this.props.mapInstance.id} />
                            <CenterMapLayerButton
                                mapInstanceId={this.props.mapInstance.id}
                                zoomPoint={this.state.visualReport.point}
                            />

                            <VisualReportSave
                                visualReport={this.state.visualReport}
                            />
                            <div className="divider divider--vertical-small" />
                            <button
                                onClick={this.onClose}
                                className="material-icons btn-flat-icon"
                                title="Close analysis panel"
                            >
                                close
                            </button>
                        </div>
                    </div>
                    <div className="report-iframe__iframe">
                        <iframe src={url} title="Visual report" ref={this.setIframeRef} />
                    </div>
                    <div className="report-iframe__iframe-footer">
                        <div data-tourId={HelpTourTargets.INFO_PANEL_FULL_SCREEN}>
                            <button onClick={this.onOpenInNewTab} className="btn-raised btn-rounded">
                                {this.props.intl.formatMessage({ id: 'locationAnalysis.analyzeDeal' })}
                                <i className="material-icons">arrow_forward</i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default injectIntl(ReportIframe);
